/**
 * Money. Constitution §2.
 *
 * All persisted amounts are integer cents, AUD. Displayed prices are
 * GST-inclusive (Australian convention). The display-layer pricing engine works
 * in dollars with round2() for parity with the approved prototype; conversion
 * to cents happens once, here, at the server boundary.
 */

/** Half away from zero, matching the prototype's Math.round on positives. */
export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Dollars -> integer cents. The single conversion point. */
export function toCents(dollars: number): number {
  return Math.round(dollars * 100);
}

export function toDollars(cents: number): number {
  return cents / 100;
}

/** round_half_up over integers, for the GST split. */
function roundHalfUp(n: number): number {
  return Math.floor(n + 0.5);
}

/**
 * The only GST rate this arithmetic implements.
 *
 * `exGst` divides by 11 and multiplies by 10. That is not "10% expressed as a
 * ratio" that could be swapped for another number: it is 10% welded into the
 * expression. Constitution §2 mandates exactly this formula, so the welding is
 * deliberate.
 *
 * What was NOT deliberate: `tenant_config.group.gst_rate` exists, is loaded
 * into the tenant object, is typed `number`, and **never reaches this file**.
 * Setting it to 0.15 changes no price anywhere. A field that looks configurable
 * and is not is worse than a constant, because somebody eventually sets it and
 * believes they have changed something. R-233.
 *
 * `assertSupportedGstRate` below closes that: the config may still hold the
 * rate, and it must now agree with the arithmetic or say so.
 */
export const GST_RATE = 0.1;

/**
 * Fail when the configured rate is one this arithmetic cannot produce.
 *
 * Called where the tenant config is loaded, not here, so a misconfiguration is
 * caught once at the boundary rather than on every price.
 */
export function assertSupportedGstRate(rate: number): void {
  if (rate !== GST_RATE) {
    throw new Error(
      `tenant_config.group.gst_rate is ${rate}, and the money helpers implement ${GST_RATE} `
        + `only: exGst() divides by 11, which is 10% welded into the expression `
        + `(constitution §2). Changing the config alone changes no price. Either set it back `
        + `to ${GST_RATE}, or change exGst, gstComponent and the Dart port together and add `
        + `new vectors to shared/pricing-engine.md first.`,
    );
  }
}

/**
 * Ex-GST value of a GST-inclusive amount. Constitution §2:
 *   ex_gst(cents) = round_half_up(cents * 10 / 11)
 */
export function exGst(cents: number): number {
  return roundHalfUp((cents * 10) / 11);
}

/**
 * Money for display, or a word when there is none.
 *
 * `formatCents(x ?? 0)` is the shape that told a customer their approved job
 * cost **$0.00** (R-313), and told a member of staff the customer had been
 * shown $0.00 per unit (R-284). Constitution §14: absent is not zero, and a
 * price is the field where that matters most.
 *
 * Both were fixed at their call site, separately, hours apart, and a third and
 * fourth were still rendering it on the two quote LISTS. So the rule lives
 * here now. R-314.
 */
export function formatCentsOrNone(
  cents: number | null | undefined,
  none = 'Not priced',
): string {
  return cents == null ? none : formatCents(cents);
}

/**
 * The GST to add to an amount WITHOUT GST: a purchase order total (spec 012,
 * FR-1204). 10% welded in, as `exGst` is, and half up.
 */
export function gstOnTop(exGstCents: number): number {
  return roundHalfUp(exGstCents / 10);
}

/** The GST component of a GST-inclusive amount. */
export function gstComponent(cents: number): number {
  return cents - exGst(cents);
}

/**
 * Commission base. Pricing engine spec §5: the ex-GST goods value, which is the
 * order total with shipping removed. Setup fees are goods, not shipping, so
 * they stay in. Computed once, at checkout or quote conversion, written to the
 * order row and to Stripe metadata, and never recomputed (Constitution §3).
 *
 * Pass the goods total (the GST-inclusive subtotal, shipping already excluded).
 */
export function commissionBase(goodsTotalCents: number): number {
  return exGst(goodsTotalCents);
}

/** Same thing when what you hold is the grand total and the shipping line. */
export function commissionBaseFromTotal(
  totalCents: number,
  shippingCents: number,
): number {
  return exGst(totalCents - shippingCents);
}

/** Commission amount from a base and a rate in basis points (1200 = 12%). */
export function commissionAmount(baseCents: number, rateBp: number): number {
  return roundHalfUp((baseCents * rateBp) / 10000);
}

const AUD = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** '$1,092.28' from integer cents. */
export function formatCents(cents: number): string {
  return AUD.format(cents / 100);
}

const AUD4 = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

/**
 * '$1.9950' from millionths of a dollar: an average cost (spec 012, FR-1270).
 * Four decimals of a dollar, as the minutes write the scenarios.
 */
export function formatMicro(micro: number): string {
  return AUD4.format(micro / 1_000_000);
}

/** '$1,092.28' from dollars, for engine output which is dollar-denominated. */
export function formatDollars(dollars: number): string {
  return AUD.format(dollars);
}
